import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Save, Sparkles, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { PROFILE_TYPES, CATEGORY_META } from "@/i18n/translations";
import { useSeo } from "@/lib/seo";
import VerifiedBadge from "@/components/VerifiedBadge";
import MemberSubmissions from "@/pages/member/MemberSubmissions";

const inputCls = "w-full border border-tertiary bg-white px-3 py-2.5 text-sm outline-none transition-colors focus:border-champagne focus:ring-1 focus:ring-champagne";
const labelCls = "mb-2 block text-xs uppercase tracking-[0.15em] text-anthracite/50";

function ChipSelect({ options, selected, onToggle, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((o) => {
        const active = selected.includes(o.value);
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onToggle(o.value)}
            className={`rounded-full border px-3 py-1.5 text-xs transition-colors duration-200 ${
              active ? "border-champagne bg-champagne/10 text-champagne" : "border-tertiary bg-white text-anthracite/60 hover:border-champagne/50"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export default function Account() {
  const { user, logout } = useAuth();
  const { lang, L, t } = useLang();
  const navigate = useNavigate();
  useSeo({ title: t("account_title"), lang, robots: "noindex,nofollow" });

  const [tax, setTax] = useState({ sector: [], market: [], intent: [] });
  const [profile, setProfile] = useState(null);
  const [completeness, setCompleteness] = useState(0);
  const [matches, setMatches] = useState([]);
  const [keywordsInput, setKeywordsInput] = useState("");
  const [saving, setSaving] = useState(false);

  const loadOverview = () => {
    api.get("/member/overview").then(({ data }) => {
      setProfile(data.profile || {});
      setCompleteness(data.completeness || 0);
      setKeywordsInput((data.profile?.keywords || []).join(", "));
    }).catch(() => {});
    api.get("/member/matches", { params: { limit: 6 } }).then(({ data }) => setMatches(data.results || [])).catch(() => {});
  };

  useEffect(() => {
    Promise.all([
      api.get("/taxonomies", { params: { type: "sector" } }),
      api.get("/taxonomies", { params: { type: "market" } }),
      api.get("/taxonomies", { params: { type: "intent" } }),
    ]).then(([s, m, i]) => setTax({ sector: s.data, market: m.data, intent: i.data })).catch(() => {});
    loadOverview();
  }, []);

  if (!profile) return <div className="mx-auto max-w-5xl px-6 py-24 text-center text-anthracite/40">…</div>;

  const set = (k, v) => setProfile((p) => ({ ...p, [k]: v }));
  const toggle = (k, val) => setProfile((p) => {
    const arr = p[k] || [];
    return { ...p, [k]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] };
  });

  const opts = (list) => list.map((x) => ({ value: x.slug, label: x[`label_${lang}`] }));
  const intentOpts = tax.intent.map((x) => ({ value: x.slug, label: x[`label_${lang}`] }));

  const save = async () => {
    setSaving(true);
    const payload = {
      profile_type: profile.profile_type || user.profile_type || "other",
      organization: profile.organization || "",
      country: profile.country || "",
      sectors: profile.sectors || [],
      countries: profile.countries || [],
      intents: profile.intents || [],
      needs: profile.needs || "",
      offers: profile.offers || "",
      keywords: keywordsInput.split(",").map((x) => x.trim()).filter(Boolean),
    };
    try {
      await api.put("/auth/profile", payload);
      toast.success(t("account_saved"));
      loadOverview();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => { await logout(); navigate("/"); };
  const ptypeLabel = PROFILE_TYPES.find((p) => p.value === (profile.profile_type || user.profile_type))?.[lang];

  return (
    <div className="mx-auto max-w-5xl px-6 py-12 lg:px-10">
      <div className="mb-10 flex flex-wrap items-start justify-between gap-4">
        <div>
          <span className="text-xs uppercase tracking-[0.3em] text-champagne">RSTI Connect</span>
          <h1 className="mt-2 font-serif text-4xl tracking-tight text-anthracite">{t("account_title")}</h1>
          <p className="mt-2 text-anthracite/60">{user.name} · <span className="text-champagne">{ptypeLabel}</span></p>
        </div>
        <button onClick={handleLogout} data-testid="account-logout" className="inline-flex items-center gap-2 border border-tertiary px-4 py-2 text-sm text-anthracite/70 hover:border-champagne">
          <LogOut className="h-4 w-4" /> {t("logout")}
        </button>
      </div>

      {/* Completeness */}
      <div className="mb-8 border border-tertiary bg-white p-5">
        <div className="mb-2 flex items-center justify-between text-sm">
          <span className="text-anthracite/60">{t("account_completeness")}</span>
          <span className="font-medium text-anthracite">{completeness}%</span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-sand">
          <div className="h-full bg-champagne transition-all duration-500" style={{ width: `${completeness}%` }} data-testid="completeness-bar" />
        </div>
      </div>

      <div className="grid gap-8 lg:grid-cols-[1.4fr_1fr]">
        {/* Profile editor */}
        <div className="space-y-6 border border-tertiary bg-white p-6" data-testid="profile-editor">
          <h2 className="font-serif text-2xl text-anthracite">{t("account_profile")}</h2>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>{t("account_type")}</label>
              <select value={profile.profile_type || user.profile_type} onChange={(e) => set("profile_type", e.target.value)} data-testid="profile-type" className={inputCls}>
                {PROFILE_TYPES.map((p) => <option key={p.value} value={p.value}>{p[lang]}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>{t("account_org")}</label>
              <input value={profile.organization || ""} onChange={(e) => set("organization", e.target.value)} data-testid="profile-org" className={inputCls} />
            </div>
          </div>

          <div>
            <label className={labelCls}>{t("account_sectors")}</label>
            <ChipSelect options={opts(tax.sector)} selected={profile.sectors || []} onToggle={(v) => toggle("sectors", v)} testid="chips-sectors" />
          </div>
          <div>
            <label className={labelCls}>{t("account_markets")}</label>
            <ChipSelect options={opts(tax.market)} selected={profile.countries || []} onToggle={(v) => toggle("countries", v)} testid="chips-markets" />
          </div>
          <div>
            <label className={labelCls}>{t("account_intents")}</label>
            <ChipSelect options={intentOpts} selected={profile.intents || []} onToggle={(v) => toggle("intents", v)} testid="chips-intents" />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelCls}>{t("account_needs")}</label>
              <textarea rows={3} value={profile.needs || ""} onChange={(e) => set("needs", e.target.value)} data-testid="profile-needs" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>{t("account_offers")}</label>
              <textarea rows={3} value={profile.offers || ""} onChange={(e) => set("offers", e.target.value)} data-testid="profile-offers" className={inputCls} />
            </div>
          </div>

          <div>
            <label className={labelCls}>{t("account_keywords")}</label>
            <input value={keywordsInput} onChange={(e) => setKeywordsInput(e.target.value)} placeholder="ex: export, agro, financement…" data-testid="profile-keywords" className={inputCls} />
          </div>

          <button onClick={save} disabled={saving} data-testid="profile-save" className="inline-flex items-center gap-2 bg-anthracite px-6 py-3 text-sm font-medium text-ivory transition-colors hover:bg-black disabled:opacity-50">
            <Save className="h-4 w-4" /> {saving ? "…" : t("account_save")}
          </button>
        </div>

        {/* Matching preview */}
        <div className="space-y-4">
          <div className="border border-champagne/30 bg-champagne/5 p-5" data-testid="matching-preview">
            <div className="mb-3 flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-champagne" />
              <h3 className="font-serif text-xl text-anthracite">{t("account_matches")}</h3>
            </div>
            <p className="mb-4 text-xs leading-relaxed text-anthracite/60">{t("account_matches_soon")}</p>
            {matches.length === 0 ? (
              <p className="text-sm text-anthracite/40">{lang === "fr" ? "Complétez votre profil pour voir des correspondances." : "Complete your profile to see matches."}</p>
            ) : (
              <div className="space-y-2">
                {matches.map((m) => (
                  <Link key={m.id} to={`/listing/${m.id}`} className="block border border-tertiary bg-white p-3 transition-colors hover:border-champagne">
                    <div className="mb-1 flex items-center justify-between gap-2">
                      <span className="text-[10px] uppercase tracking-[0.2em] text-champagne">{CATEGORY_META[m.category]?.[lang]}</span>
                      <VerifiedBadge level={m.verification?.level} />
                    </div>
                    <p className="text-sm font-medium text-anthracite line-clamp-1">{L(m, "title")}</p>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-8">
        <MemberSubmissions />
      </div>
    </div>
  );
}
